import { RemoteDataSource, RemoteDataSourceOptions } from "./RemoteDataSource";
import { Common } from "../helpers/typeUtils";
import { RemoteOneChildDataSource } from "./RemoteOneChildDataSource";
import { RemoteListChildDataSource } from "./RemoteListChildDataSource";
import { RemoteOneDataSource } from "./RemoteOneDataSource";
import { LoggerFactory } from "../logger";

export type AnyChildDataSource = Common<
  RemoteOneChildDataSource<any, any>,
  RemoteListChildDataSource<any, any>
>;
export interface RemoteChildDataSourceOptions extends RemoteDataSourceOptions {
  parent: RemoteOneDataSource<any, any>;
  parentChangedStrategy?: (ds: AnyChildDataSource) => void;
  dtoStrategy?: (ds: AnyChildDataSource, dto: any) => any;
}

const log = LoggerFactory.create("RemoteChildDataSource");

export class RemoteChildDataSource<T, ID> extends RemoteDataSource<T, ID> {
  private parentChangedStrategy: ((ds: AnyChildDataSource) => void) | undefined;
  private dtoStrategy: ((ds: AnyChildDataSource, dto: any) => any) | undefined;
  private parent: RemoteOneDataSource<any, any>;

  public static clone<X, Y>(
    old: RemoteDataSource<X, Y>,
    options?: Partial<RemoteChildDataSourceOptions>
  ): RemoteChildDataSource<X, Y> {
    options = options || {};
    let parent, oldParentChangedStrategy, oldDtoStrategy;
    if (old instanceof RemoteChildDataSource) {
      parent = old.parent;
      oldParentChangedStrategy = old.parentChangedStrategy;
      oldDtoStrategy = old.dtoStrategy;
    } else {
      parent = options.parent;
    }

    if (!parent) {
      throw new Error("parent not set");
    }

    log.debug("cloning", old, options);

    return RemoteChildDataSource.create<X, Y>({
      api: options.api || old.api,
      name: options.name || old.name,
      schema: options.schema || old.schema,
      url: options.url || old.url,
      commons: options.commons || old.commons,
      parent: parent,
      dtoStrategy: options.dtoStrategy || oldDtoStrategy,
      parentChangedStrategy:
        options.parentChangedStrategy || oldParentChangedStrategy,
      params: options.params || old.listDataSource.params
    });
  }

  public static create<T, ID>(
    options: RemoteChildDataSourceOptions
  ): RemoteChildDataSource<T, ID> {
    const it = new RemoteChildDataSource<T, ID>(options);

    it.listDataSource = new RemoteListChildDataSource(options);
    it.oneDataSource = new RemoteOneChildDataSource({ ...options });

    return it;
  }

  protected constructor(options: RemoteChildDataSourceOptions) {
    super(options);
    this.parent = options.parent;
    this.parentChangedStrategy = options.parentChangedStrategy;
    this.dtoStrategy = options.dtoStrategy;
  }
}
